import { FC } from "preact/compat";
import { format, isSameDay } from "date-fns";
import { Link } from "react-router-dom";
import { me } from "~/core/store";
import { IconButton } from "~/components";
import useWidget from "../../../Desktop/widgets/CalendarSchedule/useWidget";
import { CurrentTime, ExternalEvent } from "../../../shared";
import { SingleWidgetProps } from "../interfaces";
import ArrowRightIcon from "@material-design-icons/svg/outlined/arrow_right.svg";
import CalendarMonthIcon from "@material-design-icons/svg/outlined/calendar_month.svg";
import LeftIcon from "@material-design-icons/svg/outlined/chevron_left.svg";
import RightIcon from "@material-design-icons/svg/outlined/chevron_right.svg";
import SyncDisabledIcon from "@material-design-icons/svg/outlined/sync_disabled.svg";
import GoogleIcon from "~/assets/google.svg";
import OutlookIcon from "~/assets/outlook.svg";

const STUB_FN = () => {};

const Widget: FC<SingleWidgetProps> = ({ className }) => {
    const {
        isLoading,
        isNotSynced,
        isConnectCTA,
        canGoNow,
        now,
        selectedDay,
        upcomingId,
        externals,
        service,
        handlePrevious,
        handleNext,
        handleNow,
    } = useWidget({
        id: "calendar-schedule-mobile",
        onLink: STUB_FN,
    });
    const Icon =
        service === "outlook"
            ? OutlookIcon
            : service === "google_cal"
              ? GoogleIcon
              : CalendarMonthIcon;

    return (
        <div className={className}>
            <div className="flex justify-between items-center px-2 py-1 border-b border-gray-200">
                <div className="flex items-center gap-x-2 text-gray-500 font-bold px-2 py-1">
                    <Icon width="20" height="20" />
                    <span>Calendar</span>
                </div>
                <div className="flex gap-x-2 items-center">
                    <IconButton
                        flat
                        title="Previous day"
                        onClick={handlePrevious}
                    >
                        <LeftIcon />
                    </IconButton>
                    <button
                        type="button"
                        disabled={!canGoNow}
                        title="Go to today"
                        onClick={handleNow}
                        className="justify-center font-bold flex gap-x-2"
                    >
                        <CurrentTime now={now} />
                        <span className="min-w-[90px]">
                            {format(selectedDay.value, "EEE, dd MMM")}
                        </span>
                    </button>
                    <IconButton flat title="Next day" onClick={handleNext}>
                        <RightIcon />
                    </IconButton>
                </div>
            </div>
            <div className="p-4 min-h-20">
                {isConnectCTA ? (
                    <div className="flex flex-col items-center justify-center text-sm">
                        <Link
                            className="underline text-sky-500"
                            to={me("profile?tab=settings")}
                        >
                            Connect your{" "}
                            <OutlookIcon
                                width="16"
                                height="16"
                                className="inline-block"
                            />{" "}
                            Outlook or{" "}
                            <GoogleIcon
                                width="16"
                                height="16"
                                className="inline-block"
                            />{" "}
                            Google account
                        </Link>
                        <p className="mt-1 text-center text-gray-500">
                            in order to see upcoming schedule for today.
                        </p>
                    </div>
                ) : (
                    <div>
                        {externals.value.map((external) => (
                            <ExternalEvent
                                key={external.id}
                                external={external}
                                upcomingId={upcomingId}
                            />
                        ))}
                        {externals.value.length === 0 && !isLoading ? (
                            <div className="flex items-center justify-center text-gray-500">
                                <p>No events for today</p>
                            </div>
                        ) : !isLoading &&
                          !upcomingId &&
                          isSameDay(selectedDay.value, now.value) ? (
                            <div className="flex">
                                <div className="w-6 min-w-6 fill-current text-gray-700">
                                    <ArrowRightIcon width="24" height="24" />
                                </div>
                                <p className="ml-2 text-gray-700">
                                    No upcoming events for now
                                </p>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
            {isNotSynced && (
                <div className="absolute bottom-2 right-2">
                    <Link
                        title="Your calendar is not connected in your profile"
                        to={me("profile?tab=settings")}
                    >
                        <SyncDisabledIcon
                            width="20"
                            height="20"
                            className="fill-amber-500"
                        />
                    </Link>
                </div>
            )}
        </div>
    );
};

export default Widget;
